import React, { useState } from 'react';
import {
    Avatar,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogBody,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Input,
    Text,
    Tooltip,
    makeStyles,
    shorthands,
} from '@fluentui/react-components';
import { DeleteRegular, EditRegular } from '@fluentui/react-icons';
import { NexusAgent } from '../../../../domain/agents/models/Agent';
import { customColors, customFonts } from '../../../../styles';

const useStyles = makeStyles({
    card: {
        width: '350px',
        height: '150px',
        ...shorthands.margin('10px'),
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'start',
        backgroundColor: '#F7F7F8',
        ...shorthands.borderLeft('5px', 'solid', '#D9D9D9'),
        ...shorthands.transition('backgroundColor', '0.3s', 'ease-in-out'),
        position: 'relative',
        boxSizing: 'border-box',
    },
    content: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        ...shorthands.padding('0', '12px', '34px', '12px'),
    },
    image: {
        width: '72px',
        height: '72px',
        marginRight: '12px',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        ...shorthands.border('2px', 'solid', customColors.newChatHoverLightBlue),
        flexShrink: 0,
    },
    textContent: {
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
    },
    headerText: {
        display: '-webkit-box',
        '-webkit-line-clamp': '1',
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        textAlign: 'left',
        fontWeight: '600',
        fontSize: '20px',
        fontFamily: customFonts.TitleFont,
        color: '#0a5782',
        marginBottom: '5px',
    },
    descriptionText: {
        display: '-webkit-box',
        '-webkit-line-clamp': '3',
        '-webkit-box-orient': 'vertical',
        overflow: 'hidden',
        fontWeight: '400',
        fontSize: '12px',
        lineHeight: '1rem',
        textAlign: 'left',
        color: '#696969',
        fontFamily: customFonts.Lato,
    },
    badge: {
        fontSize: '11px',
        color: '#B42318',
        fontFamily: customFonts.Barlow,
        fontWeight: '700',
        marginBottom: '6px',
    },
    actionRow: {
        position: 'absolute',
        bottom: '10px',
        left: '12px',
        right: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    iconButton: {
        minWidth: 'unset',
        ...shorthands.border('1px', 'solid', '#C7CED4'),
        ...shorthands.borderRadius('5px'),
        backgroundColor: customColors.white,
        color: customColors.buttonTextDarkBlue,
    },
    hardDeleteButton: {
        minWidth: 'unset',
        ...shorthands.border('1px', 'solid', '#F97066'),
        ...shorthands.borderRadius('5px'),
        backgroundColor: '#FFF5F5',
        color: '#B42318',
    },
    deleteActions: {
        display: 'flex',
        ...shorthands.gap('8px'),
    },
    dialogTitle: {
        fontFamily: customFonts.TitleFont,
        color: customColors.summarizeButtonDarker,
    },
    dialogText: {
        fontFamily: customFonts.Lato,
    },
});

export interface ManageAgentTileProps {
    agent: NexusAgent;
    avatarSrc: string;
    isSoftDeleted: boolean;
    onEdit: (agentId: string) => void;
    onSoftDelete: (agentId: string) => Promise<void>;
    onHardDelete: (agentId: string) => Promise<void>;
}

const ManageAgentTile: React.FC<ManageAgentTileProps> = ({
    agent,
    avatarSrc,
    isSoftDeleted,
    onEdit,
    onSoftDelete,
    onHardDelete,
}) => {
    const styles = useStyles();
    const [isSoftDeleteOpen, setIsSoftDeleteOpen] = useState(false);
    const [isHardDeleteOpen, setIsHardDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmValue, setConfirmValue] = useState('');

    const isHardDeleteValid = confirmValue.trim() === agent.name;

    const handleSoftDelete = async (): Promise<void> => {
        setIsDeleting(true);
        try {
            await onSoftDelete(agent.agentId);
            setIsSoftDeleteOpen(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleHardDelete = async (): Promise<void> => {
        if (!isHardDeleteValid) {
            return;
        }

        setIsDeleting(true);
        try {
            await onHardDelete(agent.agentId);
            setIsHardDeleteOpen(false);
            setConfirmValue('');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Card className={styles.card}>
            <div className={styles.content}>
                <Avatar className={styles.image} image={{ src: avatarSrc }} shape="square" />

                <div className={styles.textContent}>
                    {isSoftDeleted && <Text className={styles.badge}>Soft deleted</Text>}
                    <Text className={styles.headerText}>{agent.name}</Text>
                    <Text className={styles.descriptionText}>{agent.shortDescription ?? agent.description}</Text>
                </div>
            </div>

            <div className={styles.actionRow}>
                <div className={styles.deleteActions}>
                    <Dialog open={isSoftDeleteOpen} onOpenChange={(_e, d) => setIsSoftDeleteOpen(d.open)}>
                        <DialogTrigger disableButtonEnhancement>
                            <Tooltip content="Soft delete" relationship="label">
                                <Button className={styles.iconButton} icon={<DeleteRegular />} />
                            </Tooltip>
                        </DialogTrigger>
                        <DialogSurface>
                            <DialogBody>
                                <DialogTitle className={styles.dialogTitle}>Soft delete {agent.name}?</DialogTitle>
                                <Text className={styles.dialogText}>
                                    This can be restored later by service-level tooling.
                                </Text>
                                <DialogActions>
                                    <DialogTrigger disableButtonEnhancement>
                                        <Button appearance="secondary">Cancel</Button>
                                    </DialogTrigger>
                                    <Button
                                        appearance="primary"
                                        onClick={() => void handleSoftDelete()}
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? 'Deleting...' : 'Soft Delete'}
                                    </Button>
                                </DialogActions>
                            </DialogBody>
                        </DialogSurface>
                    </Dialog>

                    <Dialog open={isHardDeleteOpen} onOpenChange={(_e, d) => setIsHardDeleteOpen(d.open)}>
                        <DialogTrigger disableButtonEnhancement>
                            <Tooltip content="Hard delete" relationship="label">
                                <Button className={styles.hardDeleteButton} icon={<DeleteRegular />} />
                            </Tooltip>
                        </DialogTrigger>
                        <DialogSurface>
                            <DialogBody>
                                <DialogTitle className={styles.dialogTitle}>Hard delete {agent.name}?</DialogTitle>
                                <Text className={styles.dialogText}>
                                    This permanently removes the agent. Type the exact agent name to confirm.
                                </Text>
                                <Input
                                    value={confirmValue}
                                    placeholder={agent.name}
                                    onChange={(event) => setConfirmValue(event.target.value)}
                                />
                                <DialogActions>
                                    <DialogTrigger disableButtonEnhancement>
                                        <Button appearance="secondary">Cancel</Button>
                                    </DialogTrigger>
                                    <Button
                                        appearance="primary"
                                        onClick={() => void handleHardDelete()}
                                        disabled={!isHardDeleteValid || isDeleting}
                                    >
                                        {isDeleting ? 'Deleting...' : 'Delete Permanently'}
                                    </Button>
                                </DialogActions>
                            </DialogBody>
                        </DialogSurface>
                    </Dialog>
                </div>

                <Tooltip content="Edit agent" relationship="label">
                    <Button
                        className={styles.iconButton}
                        icon={<EditRegular />}
                        onClick={() => onEdit(agent.agentId)}
                    />
                </Tooltip>
            </div>
        </Card>
    );
};

export default ManageAgentTile;
